// src/hooks/useRankingData.js
/**
 * useRankingData
 * --------------------------------------------------------------------
 * 목적:
 *  - 랭킹 화면 전반에서 공통으로 쓰는 랭킹/날짜/매장/로딩 상태를
 *    Redux 스토어에서 구독하고, 날짜(date) 변경 시 랭킹 데이터를
 *    자동으로 재요청(fetchRanking)하는 커스텀 훅.
 *
 * 동작 요약:
 *  - Redux store의 ranking 슬라이스를 useSelector로 구독.
 *  - 현재 date가 바뀌면 useEffect에서 fetchRanking(date) 디스패치.
 *  - 화면에서 자주 쓰는 키를 의미 있는 이름으로 매핑하여 반환.
 *
 * 반환값:
 *  {
 *    currentDate,      // 선택/현재 기준일(문자열; formatDate 적용 가정)
 *    storeId,          // 매장 식별자
 *    rankDetailValue,  // { daily, weekly, monthly } 각 배열
 *    lastDetailValue,  // 직전(비교) 기간의 { daily, weekly, monthly }
 *    isLoadingState,   // 로딩 플래그
 *    error,            // 에러 객체(또는 null)
 *    validDateList,    // 선택 가능한 유효 날짜 목록(달력에 표시)
 *  }
 *
 */

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRanking } from "../store/slices/rankingSlice";
import { formatDate } from "../functions/DateConverter";

export default function useRankingData() {
  const dispatch = useDispatch();

  // ranking 슬라이스에서 필요한 필드만 구조분해 할당
  const {
    date, // 기준 날짜(문자열; 예: '2025-08-11')
    storeId, // 매장 ID
    rankDetail, // 현재 기간 데이터: { daily: [], weekly: [], monthly: [] }
    lastDetail, // 비교 기간 데이터: { daily: [], weekly: [], monthly: [] }
    isLoading, // 로딩 상태
    error, // 에러 정보
    validDateList, // 달력에서 선택 가능한 유효 날짜 배열(백엔드 제공 가정)
  } = useSelector((state) => state.ranking);

  useEffect(() => {
    // date가 바뀔 때마다 해당 날짜 기준으로 랭킹 데이터 요청
    // 주의: date가 null/undefined가 아니어야 함(초기값은 슬라이스에서 보장)
    dispatch(fetchRanking(date));
  }, [dispatch, date]);

  // 뷰에서 쓰기 편한 키로 리턴(네이밍 통일)
  return {
    currentDate: date,
    storeId,
    rankDetailValue: rankDetail,
    lastDetailValue: lastDetail,
    isLoadingState: isLoading,
    error,
    validDateList,
  };
}
